/**
 * InvoicesPage
 *
 * List page for all invoices with quick filters and actions.
 */

import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import PaymentIcon from '@mui/icons-material/Payment';
import { DataTable, EntityListResults, LegacySourceIcon, SearchField } from '@/components/common';
import { useDebounce } from '@/hooks/useDebounce';
import { useInvoicesPage, useMarkInvoiceSent, useOverdueSummary, printInvoicePdf, invoiceWorkOrderRef, type InvoiceListItem, type InvoiceListSortField, type InvoiceSortDirection, type InvoiceStatus } from '@/hooks/useInvoices';
import { CreateStandaloneInvoiceDialog } from './CreateStandaloneInvoiceDialog';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { INVOICE_READ_PERMISSIONS } from '@/lib/invoicePermissions';
import { RecordPaymentDialog } from '@/components/workorders/RecordPaymentDialog';
import {
  INVOICE_PAYMENT_METHOD_FILTERS,
  invoicePaymentMethodKeys,
  type InvoicePaymentMethodFilter,
} from './invoicePaymentMethods';
import { formatDate } from '@/utils/locale';


const STATUS_COLORS: Record<InvoiceStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'error',
  void: 'default',
  refunded: 'default',
  credited: 'default',
};

type FilterValue = 'all' | 'open' | 'overdue' | 'paid' | 'voided';

const FILTER_STATUS_MAP: Record<FilterValue, string | undefined> = {
  all: undefined,
  open: 'draft,sent',
  overdue: 'overdue',
  paid: 'paid,partial',
  voided: 'void',
};

function paymentMethodLabel(
  item: InvoiceListItem,
  tc: (key: string) => string,
): string {
  const methods = invoicePaymentMethodKeys(item);
  if (methods.length === 0) return '—';
  return methods.map((method) => tc(`paymentMethod.${method}`)).join(' + ');
}

/**
 * Column key → API sort field. Customer and payment method are absent: the
 * name comes from work-order parties and the method from payment rows, so the
 * server rejects them (#2502). Those two reorder the loaded page only.
 */
const SERVER_SORT_API = {
  invoiceNumber: 'invoiceNumber',
  total: 'totalCents',
  paid: 'paidAmountCents',
  status: 'status',
  dueDate: 'dueDate',
  issueDate: 'issueDate',
} as const satisfies Record<string, InvoiceListSortField>;

type ServerSortColumn = keyof typeof SERVER_SORT_API;
type PageLocalSortKey = 'customer' | 'paymentMethod';

function isServerSortColumn(key: string): key is ServerSortColumn {
  return Object.prototype.hasOwnProperty.call(SERVER_SORT_API, key);
}

/** API sort field → the column key DataTable marks as sorted. */
function columnForSortField(sortBy: string | undefined): ServerSortColumn {
  const match = (Object.entries(SERVER_SORT_API) as [ServerSortColumn, InvoiceListSortField][]).find(
    ([, field]) => field === sortBy,
  );
  return match?.[0] ?? 'issueDate';
}

function isPageLocalSortKey(key: string): key is PageLocalSortKey {
  return key === 'customer' || key === 'paymentMethod';
}

function comparePageLocal(a: InvoiceListItem, b: InvoiceListItem, sortKey: PageLocalSortKey): number {
  if (sortKey === 'customer') {
    return a.customerName.localeCompare(b.customerName);
  }
  return invoicePaymentMethodKeys(a).join(',').localeCompare(invoicePaymentMethodKeys(b).join(','));
}

export default function InvoicesPage() {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const markSent = useMarkInvoiceSent();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const hasAnyPermission = useAuthStore((s) => s.hasAnyPermission);
  // Invoice administration and checkout are separate least-privilege capabilities.
  const canManageInvoices = hasPermission('invoices.manage');
  const canTakePayments = hasPermission('payments.take') || canManageInvoices;
  const canReadSettlements = hasAnyPermission(INVOICE_READ_PERMISSIONS);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300).trim();
  const [filter, setFilter] = useState<FilterValue>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<'all' | InvoicePaymentMethodFilter>('all');
  const [paymentInvoice, setPaymentInvoice] = useState<InvoiceListItem | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pageLocalSort, setPageLocalSort] = useState<{
    key: PageLocalSortKey;
    direction: InvoiceSortDirection;
  } | null>(null);

  const invoiceFilters = useMemo(() => {
    const next: { q?: string; status?: string; paymentMethod?: string } = {};
    if (debouncedSearch) next.q = debouncedSearch;
    const status = FILTER_STATUS_MAP[filter];
    if (status) next.status = status;
    if (paymentMethodFilter !== 'all') next.paymentMethod = paymentMethodFilter;
    return next;
  }, [debouncedSearch, filter, paymentMethodFilter]);

  // The shared paging hook resets to page 0 when the filters or sort change:
  // page 4 of the old result set is not page 4 of the new one (#2744).
  // Default order is issue date descending; that column is not on the table.
  const {
    rows: invoiceList,
    total: totalInvoices,
    isLoading,
    isError,
    error,
    refetch,
    sort,
    setSort,
    pagination,
  } = useInvoicesPage(invoiceFilters);
  const { data: overdueSummary } = useOverdueSummary();

  const displayedInvoices = useMemo(() => {
    if (!pageLocalSort) return invoiceList;
    const { key, direction } = pageLocalSort;
    return [...invoiceList].sort((a, b) => {
      const comparison = comparePageLocal(a, b, key);
      return direction === 'asc' ? comparison : -comparison;
    });
  }, [invoiceList, pageLocalSort]);

  const handleSortChange = (key: string, direction: InvoiceSortDirection) => {
    if (isPageLocalSortKey(key)) {
      // The request stays on the server sort. This reorders the loaded page.
      setPageLocalSort({ key, direction });
      return;
    }
    if (!isServerSortColumn(key)) return;
    setPageLocalSort(null);
    setSort({ sortBy: SERVER_SORT_API[key], sortDir: direction });
  };

  const handlePrint = (invoiceId: string) => {
    void printInvoicePdf(invoiceId).then((result) => {
      if (result.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
    }).catch(() => {
      notify.error(t('error.generic'));
    });
  };

  const handleMarkSent = async (invoiceId: string, workOrderId: string | null) => {
    try {
      await markSent.mutateAsync({ invoiceId, ...invoiceWorkOrderRef(workOrderId) });
      notify.success(t('actions.markSentSuccess'));
    } catch {
      // error handled by mutation
    }
  };

  const handleRowClick = (item: InvoiceListItem) => {
    navigate(`/invoices/${item.id}`);
  };

  const quickFilters = [
    { label: t('filters.all'), value: 'all' },
    { label: t('filters.open'), value: 'open' },
    { label: t('filters.overdue'), value: 'overdue' },
    { label: t('filters.paid'), value: 'paid' },
    { label: t('filters.voided'), value: 'voided' },
  ];

  const columns = [
    {
      header: t('columns.invoiceNumber'),
      sortKey: 'invoiceNumber',
      renderCell: (item: InvoiceListItem) => (
        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
          {item.invoiceNumber}
        </Typography>
      ),
    },
    {
      header: `${t('columns.customer')} (${t('sort.thisPage')})`,
      sortKey: 'customer',
      renderCell: (item: InvoiceListItem) => item.customerName,
    },
    {
      header: t('columns.vehicle'),
      renderCell: (item: InvoiceListItem) => item.vehicleReg.trim() || '—',
    },
    {
      header: t('columns.total'),
      sortKey: 'total',
      align: 'right' as const,
      renderCell: (item: InvoiceListItem) => formatPrice(item.totalCents),
    },
    {
      header: t('columns.paid'),
      sortKey: 'paid',
      align: 'right' as const,
      renderCell: (item: InvoiceListItem) => (
        <Typography variant="body2" color={item.paidAmountCents > 0 ? 'success.main' : 'text.secondary'}>
          {formatPrice(item.paidAmountCents)}
        </Typography>
      ),
    },
    {
      header: `${t('columns.paymentMethod')} (${t('sort.thisPage')})`,
      sortKey: 'paymentMethod',
      renderCell: (item: InvoiceListItem) => paymentMethodLabel(item, tc),
    },
    {
      header: t('columns.status'),
      sortKey: 'status',
      renderCell: (item: InvoiceListItem) => (
        <Chip
          size="small"
          label={t(`status.${item.status}`)}
          color={STATUS_COLORS[item.status] ?? 'default'}
        />
      ),
    },
    {
      header: t('columns.dueDate'),
      sortKey: 'dueDate',
      renderCell: (item: InvoiceListItem) => {
        if (!item.dueDate) return '—';
        const due = new Date(item.dueDate);
        // Prefer the server-derived flag (#1038); fall back to client date math.
        const isOverdue = item.isOverdue ?? (due < new Date() && item.status !== 'paid' && item.status !== 'void');
        return (
          <Typography variant="body2" color={isOverdue ? 'error.main' : 'text.primary'} fontWeight={isOverdue ? 600 : 400}>
            {formatDate(due)}
          </Typography>
        );
      },
    },
    {
      header: '',
      width: 120,
      renderCell: (item: InvoiceListItem) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('actions.print')}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handlePrint(item.id); }}
              aria-label={t('actions.print')}
            >
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageInvoices && item.status === 'draft' && (
            <Tooltip title={t('actions.markSent')}>
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); void handleMarkSent(item.id, item.bookingId); }}
                  disabled={markSent.isPending}
                  aria-label={t('actions.markSent')}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {canTakePayments && item.status !== 'paid' && item.status !== 'void' && (
            <Tooltip title={t('actions.recordPayment')}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setPaymentInvoice(item); }}
                aria-label={t('actions.recordPayment')}
              >
                <PaymentIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
    {
      header: '',
      width: 40,
      align: 'right' as const,
      renderCell: (item: InvoiceListItem) => <LegacySourceIcon source={item.legacySource} />,
    },
  ];

  const renderMobileCard = (item: InvoiceListItem) => {
    const isOverdue = item.isOverdue ?? (item.dueDate && new Date(item.dueDate) < new Date() && item.status !== 'paid' && item.status !== 'void');
    return (
      <Card variant="outlined" sx={{ mb: 1 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
              {item.invoiceNumber}
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Chip
                size="small"
                label={t(`status.${item.status}`)}
                color={STATUS_COLORS[item.status] ?? 'default'}
              />
              <LegacySourceIcon source={item.legacySource} />
            </Stack>
          </Stack>
          <Typography variant="body2">{item.customerName}</Typography>
          <Typography variant="caption" color="text.secondary">{item.vehicleReg.trim() || '—'}</Typography>
          <Stack direction="row" justifyContent="space-between" mt={1}>
            <Typography variant="body2" fontWeight={600}>{formatPrice(item.totalCents)}</Typography>
            {item.dueDate && (
              <Typography variant="caption" color={isOverdue ? 'error.main' : 'text.secondary'}>
                {formatDate(item.dueDate)}
              </Typography>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {paymentMethodLabel(item, tc)}
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => handlePrint(item.id)}>
              {t('actions.print')}
            </Button>
            {canTakePayments && item.status !== 'paid' && item.status !== 'void' && (
              <Button size="small" variant="contained" startIcon={<PaymentIcon />} onClick={() => setPaymentInvoice(item)}>
                {t('actions.recordPayment')}
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            {t('subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {canManageInvoices && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              data-testid="create-invoice-button"
            >
              {t('actions.createInvoice')}
            </Button>
          )}
          {canManageInvoices && (
            <Button
              variant="outlined"
              startIcon={<PaymentIcon />}
              onClick={() => navigate('/invoices/payments')}
              data-testid="payment-matching-link"
            >
              {t('paymentMatching.open')}
            </Button>
          )}
          {(hasPermission('reports.read') || canManageInvoices) && (
            <Button variant="outlined" onClick={() => navigate('/invoices/exports')}>
              {t('export.title')}
            </Button>
          )}
          {canReadSettlements && (
            <Button variant="outlined" onClick={() => navigate('/invoices/card-terminal-reconciliation')} data-testid="card-terminal-reconciliation-link">
              {t('terminalReconciliation.open')}
            </Button>
          )}
          {canReadSettlements && (
            <Button
              variant="outlined"
              onClick={() => navigate('/invoices/svea-settlements')}
              data-testid="svea-settlements-link"
            >
              {t('sveaSettlements.open')}
            </Button>
          )}
        </Stack>
      </Stack>

      {overdueSummary && overdueSummary.count > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 3, cursor: 'pointer' }}
          onClick={() => setFilter('overdue')}
          data-testid="overdue-summary"
        >
          {t('overdueSummary', {
            count: overdueSummary.count,
            amount: (overdueSummary.outstandingCents / 100).toFixed(2).replace('.', ','),
          })}
        </Alert>
      )}

      <SearchField
        value={search}
        onChange={setSearch}
        placeholder={t('searchPlaceholder')}
      />

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
      >
        {quickFilters.map((item) => (
          <Chip
            key={item.value}
            label={item.label}
            color={filter === item.value ? 'primary' : 'default'}
            variant={filter === item.value ? 'filled' : 'outlined'}
            onClick={() => setFilter(item.value as FilterValue)}
            size="small"
          />
        ))}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <FormControl size="small" sx={{ minWidth: { xs: 160, md: 220 } }}>
          <InputLabel id="invoice-payment-method-filter-label">{t('columns.paymentMethod')}</InputLabel>
          <Select
            labelId="invoice-payment-method-filter-label"
            value={paymentMethodFilter}
            label={t('columns.paymentMethod')}
            onChange={(e: SelectChangeEvent) =>
              setPaymentMethodFilter(e.target.value as 'all' | InvoicePaymentMethodFilter)
            }
            data-testid="invoice-payment-method-filter"
          >
            <MenuItem value="all">{t('filters.all')}</MenuItem>
            {INVOICE_PAYMENT_METHOD_FILTERS.map((method) => (
              <MenuItem key={method} value={method}>
                {tc(`paymentMethod.${method}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {pageLocalSort && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="invoice-page-sort-note">
          {t('sort.pageOnly')}
        </Alert>
      )}

      {/* Above the table, not in its place: the status chips live in the
          DataTable, and hiding them would leave no way off a failing filter. */}
      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => { void refetch(); }}>
              {tc('errors.refresh')}
            </Button>
          }
        >
          {t('errorLoading')}
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </Alert>
      )}

      <EntityListResults
        loading={isLoading}
        resultCountText={t('resultCount', { count: totalInvoices })}
        pagination={pagination}
      >
      <DataTable
        columns={columns}
        data={displayedInvoices}
        getRowKey={(item) => item.id}
        sortKey={pageLocalSort?.key ?? columnForSortField(sort?.sortBy)}
        sortDirection={pageLocalSort?.direction ?? sort?.sortDir ?? 'desc'}
        onSortChange={handleSortChange}
        activeFilter={filter}
        onFilterChange={(v) => setFilter(v as FilterValue)}
        renderMobileCard={renderMobileCard}
        onRowClick={handleRowClick}
        loading={isLoading}
        emptyMessage={debouncedSearch ? tc('noResults') : t('noInvoices')}
        emptyDescription={debouncedSearch ? t('noSearchResultsDesc') : t('noInvoicesDesc')}
        ariaLabel={t('title')}
      />

      </EntityListResults>

      {paymentInvoice && (
        <RecordPaymentDialog
          open={!!paymentInvoice}
          onClose={() => setPaymentInvoice(null)}
          invoiceId={paymentInvoice.id}
          invoiceNumber={paymentInvoice.invoiceNumber}
          totalCents={paymentInvoice.totalCents}
          paidAmountCents={paymentInvoice.paidAmountCents}
          {...invoiceWorkOrderRef(paymentInvoice.bookingId)}
        />
      )}

      <CreateStandaloneInvoiceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Box>
  );
}
